import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useMaintenance } from "@/hooks/useMaintenance";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { MaintenancePage } from "./MaintenancePage";

const BYPASS_PATHS = ["/admin", "/.lovable/oauth/consent", "/login"];

/**
 * Full maintenance hides the site behind a page. The gate used to show a
 * spinner until the maintenance row and, for signed-in people, the admin
 * check had answered, which held every page (and unmounted a page already on
 * screen the moment sign-in resolved). Maintenance is almost always off, so
 * the page renders at once and the gate appears only once a "full" row is
 * known; the one wait left is the admin check, in full mode, so an admin is
 * not shown the closed sign for a second.
 */
export function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const { config } = useMaintenance();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const location = useLocation();

  if (config.mode !== "full") return children;
  if (BYPASS_PATHS.some((p) => location.pathname.startsWith(p))) return children;
  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isAdmin) return children;

  return <MaintenancePage config={config} />;
}
